/**
 * Source of truth for landing page content.
 * Ticket prices are placeholders until confirmed against Eventbrite live inventory.
 * Update the `tickets` array below when the client provides the current tier list.
 */

export const EVENTBRITE_URL =
  "https://www.eventbrite.co.uk/e/agentic-ai-for-finance-certification-tickets-1992777747888?aff=landing";

// VIP has its own checkout when created; for now we route to the same event with a VIP-aff param.
export const VIP_CHECKOUT_URL =
  "https://www.eventbrite.co.uk/e/agentic-ai-for-finance-certification-tickets-1992777747888?aff=landing-vip";

export const EVENT = {
  title: "Agentic AI for Finance Certification",
  eyebrow: "LIVE ONLINE CERTIFICATION",
  headline: "Build Agentic AI Systems for Real Financial Workflows",
  supporting:
    "Learn to design multimodal financial agents, engineer reliable workflows, evaluate AI outputs and apply the security boundaries required for finance.",
  dates: "August 29 — September 13, 2026",
  daysDetail: "4 live sessions · Sat/Sun cadence",
  time: "08:30 — 14:00 EDT",
  duration: "5h 30m per session",
  format: "Live online",
  instructor: "Nicole Königstein",
  certification: "Certificate of completion included",
  lowestPrice: 849.99,
  vipPrice: 1499.99,
  vipSeats: 10,
};

export const OUTCOMES = [
  {
    n: "01",
    title: "Build",
    body: "Create structured agentic workflows for financial tasks. Compose tools, memory and retrieval into reliable pipelines.",
  },
  {
    n: "02",
    title: "Analyse",
    body: "Work across financial text, tables, charts, audio and time-series. Reason over evidence, not just prompts.",
  },
  {
    n: "03",
    title: "Evaluate",
    body: "Test agent outputs for quality, reliability and risk. Trace tool usage, latency, cost and failure modes.",
  },
  {
    n: "04",
    title: "Control",
    body: "Add validation, human oversight and workflow boundaries. Ship systems that operate within finance-grade guardrails.",
  },
];

export const JOURNEY = [
  {
    n: "01",
    label: "Agentic Finance Foundations",
    body: "Understand how financial agents use tools, memory, retrieval and structured workflows. From prompt engineering to harness engineering.",
  },
  {
    n: "02",
    label: "Multimodal Financial Agents",
    body: "Work with 10-Ks, earnings calls, financial tables, charts, dashboards and time-series data across a single reasoning surface.",
  },
  {
    n: "03",
    label: "Harness & Loop Engineering",
    body: "Build workflows that plan, execute, evaluate and improve. Verification loops, structured outputs and reliable orchestration.",
  },
  {
    n: "04",
    label: "Evaluation, Security & Application",
    body: "Test reliability, define boundaries, protect sensitive data. Deploy agents that satisfy governance and audit requirements.",
  },
];

export const EXPERIENCE = [
  { n: "I", title: "Learn", body: "Understand the architecture and design principles." },
  { n: "II", title: "Build", body: "Follow practical demonstrations and complete exercises." },
  { n: "III", title: "Evaluate", body: "Test outputs for accuracy, reliability and risk." },
  { n: "IV", title: "Apply", body: "Adapt the workflows to relevant financial use cases." },
];

export const INCLUSIONS = [
  "Live expert-led sessions",
  "Hands-on coding labs",
  "Learning resources",
  "Certificate of completion",
  "Session recordings",
  "Graded capstone project",
  "Live Q&A with Nicole",
];

/**
 * TICKETS
 * The problem statement requires exact Eventbrite tier names and prices.
 * These are structured placeholders. Update `name` and `price` from live Eventbrite inventory.
 * Set `verified: true` after cross-checking.
 */
export const TICKETS = [
  {
    id: "standard",
    name: "Standard Certification Pass",
    price: 849.99,
    original: null,
    tag: null,
    deadline: null,
    inclusions: [
      "Full access to all 4 live sessions",
      "Hands-on coding labs",
      "Session recordings",
      "Certificate of completion",
      "Graded capstone project",
      "Community access",
    ],
    url: EVENTBRITE_URL,
    verified: false,
  },
];

export const VIP_TICKET = {
  id: "vip",
  name: "VIP Certification Pass",
  price: 1499.99,
  seats: 10,
  tag: "MOST PERSONALIZED EXPERIENCE",
  inclusions: [
    "Full access to the live Agentic AI for Finance Certification",
    "All benefits of the Standard Certification Pass",
    "Certificate of completion",
    "Private 30-minute one-to-one session with Nicole",
    "Discuss your finance use case, workflow or implementation directly with Nicole",
  ],
  note:
    "The one-to-one session will be scheduled separately. Session availability, scheduling process and participation terms will be shared with VIP purchasers. Advisory and educational only — not financial, investment, legal or business consulting.",
  url: VIP_CHECKOUT_URL,
};

export const AUDIENCE = [
  "Finance and investment professionals",
  "Financial analysts and FP&A teams",
  "Banking, insurance and fintech professionals",
  "Risk and compliance professionals",
  "AI and data professionals",
  "Consultants working with financial organisations",
  "Previous Packt GenAI for Finance participants",
];

export const FAQS = [
  {
    q: "Who is this certification for?",
    a: "Finance professionals, analysts, FP&A teams, risk/compliance leads, and AI/data engineers building agentic systems for financial workflows. Previous Packt GenAI for Finance participants will find this a direct next step.",
  },
  {
    q: "Do I need coding or Python experience?",
    a: "Working familiarity with Python is helpful for the labs. Nicole guides you through implementation patterns, so intermediate developers and technical finance professionals both get value.",
  },
  {
    q: "Do I need a finance background?",
    a: "Some finance context helps — examples span 10-Ks, earnings calls, portfolio analysis and compliance workflows. AI professionals without finance backgrounds are welcome and will benefit from the domain framing.",
  },
  {
    q: "Is the program delivered live?",
    a: "Yes. All four sessions are delivered live online by Nicole Königstein with live Q&A and guided labs.",
  },
  {
    q: "Will recordings be available?",
    a: "Yes — recordings of every live session are made available to registered participants.",
  },
  {
    q: "What is included in the certification?",
    a: "Four live expert-led sessions, hands-on labs, learning resources, a graded capstone project, session recordings, community access and a certificate of completion.",
  },
  {
    q: "What is included in the VIP Pass?",
    a: "Everything in the Standard Certification Pass, plus a private 30-minute one-to-one session with Nicole to discuss your finance use case, workflow or implementation. Limited to 10 participants.",
  },
  {
    q: "How will the one-to-one session with Nicole be scheduled?",
    a: "After purchase, VIP participants receive scheduling details directly. Availability, timing and terms are shared with confirmed VIP purchasers. The session is advisory and educational, not financial, investment, legal or business consulting.",
  },
];

// Marquee copy — the editorial slogan
export const MARQUEE_ITEMS = [
  "Build.",
  "Analyse.",
  "Evaluate.",
  "Control.",
  "Deploy.",
  "Govern.",
];

// Instructor imagery (real photos from QuantMinds International, in /public)
export const INSTRUCTOR_IMG = "/nicole-10.png"; // close-up — used in hero
export const INSTRUCTOR_PORTRAIT = "/nicole-9.png"; // on-stage podium
export const INSTRUCTOR_PANEL = "/nicole-11.png"; // panel discussion (wide)

export const INSTRUCTOR = {
  name: "Nicole Königstein",
  role: "Chief AI Officer & Head of Data Science",
  bio: "Nicole Königstein is a Chief AI Officer and applied researcher working at the intersection of quantitative finance and machine learning. She leads production AI teams building agentic systems for financial workflows — spanning research, portfolio analysis, compliance and enterprise finance. Nicole is a recognised international speaker on quantitative AI, presenting at events such as QuantMinds International, and has authored technical books and courses on generative AI and time-series modelling. She teaches with a practical, engineering-first perspective: how modern agents are actually built, evaluated and trusted in regulated environments.",
  speakingAt: "QuantMinds International",
};
